import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { callAIStructured, AIGatewayError } from "./ai-gateway";
import { enforceQuota, incrementUsage, QuotaExceededError } from "./lib/quota";
import { CAPABILITIES } from "@/lib/quotas";

export type TailoredBullet = {
  company: string;
  role: string;
  bullets: string[]; // rewritten, JD-aligned
};

export type TailoredResumeResult = {
  summary: string;
  experience: TailoredBullet[];
  keyMatches: string[];
  detectedKeywords: string[];
  changeNotes: string[]; // explains the diff at a high level
};

const TAILORED_RESUME_TOOL = {
  name: "save_tailored_resume",
  description:
    "Restructure the candidate's resume to align with the JD. Rewrite the summary and bullets to surface the most relevant experience first. Use ONLY facts from the candidate profile — never invent companies, metrics, or technologies.",
  parameters: {
    type: "object",
    properties: {
      summary: {
        type: "string",
        description:
          "2-3 sentence professional summary at the top, JD-aligned and grounded in real experience.",
      },
      experience: {
        type: "array",
        description:
          "Each role from the candidate's chronological experience, with rewritten JD-aligned bullets. Preserve original company/role names. Limit to top 4-5 most relevant roles.",
        items: {
          type: "object",
          properties: {
            company: { type: "string" },
            role: { type: "string" },
            bullets: {
              type: "array",
              items: { type: "string" },
              description:
                "3-5 punchy bullets per role. Lead with action verbs. Quantify when the original profile contains metrics. Reorder so JD-relevant accomplishments come first.",
            },
          },
          required: ["company", "role", "bullets"],
          additionalProperties: false,
        },
      },
      keyMatches: {
        type: "array",
        items: { type: "string" },
        description:
          "3-6 specific candidate strengths (skills, projects, achievements) that map directly to JD requirements.",
      },
      detectedKeywords: {
        type: "array",
        items: { type: "string" },
        description: "Keywords / required skills detected in the JD.",
      },
      changeNotes: {
        type: "array",
        items: { type: "string" },
        description:
          "2-4 plain-English notes explaining what was changed and why (e.g. 'Reordered Acme bullets to lead with React work', 'Added Kubernetes mention from your portfolio').",
      },
    },
    required: ["summary", "experience", "keyMatches", "detectedKeywords", "changeNotes"],
    additionalProperties: false,
  },
};

export const generateTailoredResume = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { jdText: string; jdId?: string | null }) => {
    if (!input?.jdText || input.jdText.length < 20)
      throw new Error("jdText is required (min 20 chars)");
    if (input.jdText.length > 30_000)
      throw new Error("Job description exceeds 30,000 characters");
    return input;
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    try {
      await enforceQuota(supabase, userId, CAPABILITIES.resume_generation);
    } catch (e) {
      if (e instanceof QuotaExceededError) throw new Response(e.message, { status: e.status });
      throw e;
    }

    const { data: candidateRow, error: candidateErr } = await supabase
      .from("candidate_profiles")
      .select(
        "verified_skills, chronological_experience, raw_resume_text, portfolio_data, github_metrics",
      )
      .eq("user_id", userId)
      .maybeSingle();

    if (candidateErr) {
      throw new Response(`Failed to load profile: ${candidateErr.message}`, {
        status: 500,
      });
    }

    const profileSummary = JSON.stringify(
      {
        skills: candidateRow?.verified_skills ?? [],
        experience: candidateRow?.chronological_experience ?? [],
        portfolio: candidateRow?.portfolio_data ?? {},
        github: candidateRow?.github_metrics ?? {},
        rawResume: (candidateRow?.raw_resume_text ?? "").slice(0, 6_000),
      },
      null,
      2,
    ).slice(0, 14_000);

    let result: TailoredResumeResult;
    try {
      result = await callAIStructured<TailoredResumeResult>({
        messages: [
          {
            role: "system",
            content:
              "You are an elite resume strategist. Your job: rewrite the candidate's resume to maximise relevance to the target JD. Lead with what matters. Reorder bullets so JD-aligned accomplishments come first. Tighten weak language. Use action verbs and quantified outcomes. CRITICAL: never invent companies, metrics, technologies, or projects that are not in the candidate profile. If the profile is sparse, return what you can with fewer roles rather than fabricating.",
          },
          {
            role: "user",
            content: `Target Job Description:\n${data.jdText}\n\nCandidate Profile (source of truth):\n${profileSummary}`,
          },
        ],
        tool: TAILORED_RESUME_TOOL,
      });
    } catch (e) {
      if (e instanceof AIGatewayError)
        throw new Response(e.message, { status: e.status });
      throw e;
    }

    await supabase.from("generated_materials").insert({
      user_id: userId,
      jd_id: data.jdId ?? null,
      type: "resume",
      content: result as unknown as never,
    });

    await incrementUsage(userId, CAPABILITIES.resume_generation);

    return result;
  });
